<template>
    <BaseModal
        :isVisible="modalStatus"
        title="Veículo Devolvido e Bloqueado"
        size="lg"
        @close="clean"
    >
        <template v-slot:content>
            <SectionContainer
                title="Dados da Reserva"
                icon="calendar-check"
            >
                <!-- Header com imagem e status -->
                <div class="reservation-header">
                    <div class="vehicle-section">
                        <ImageVehicle :vehicle-data="vehicleData"/>
                    </div>

                    <div class="status-section">
                        <div class="status-info">
                            <span class="status-badge status-completed">
                                Devolvido
                            </span>
                            <div class="reservation-id">
                                #{{ reserveData.id }}
                            </div>
                        </div>

                        <div class="user-info">
                            <i class="fas fa-user text-primary"></i>
                            <span>{{ reserveData.user ? reserveData.user.name : 'N/A' }}</span>
                            <button
                                class="btn btn-sm btn-outline-primary"
                                @click="openModalUser(reserveData)"
                                title="Dados do Usuário"
                            >
                                <i class="fas fa-user"></i>
                            </button>
                        </div>

                        <div class="action-buttons">
                            <button
                                class="btn btn-warning btn-block mb-2 w-100"
                                @click="openModal(reserveData)"
                                title="Visualizar Checklist"
                            >
                                <i class="fas fa-clipboard-check mr-2"></i>
                                Visualizar Checklist
                            </button>
                            <button
                                class="btn btn-success btn-block"
                                @click="unlock(vehicleData.id, vehicleData.board)"
                                title="Desbloquear Veículo"
                            >
                                <i class="fas fa-unlock mr-2"></i>
                                Desbloquear Veículo
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Grid de informações em cards -->
                <div class="info-cards-grid">
                    <div class="info-card">
                        <div class="card-icon">
                            <i class="fas fa-car-side"></i>
                        </div>
                        <div class="card-content">
                            <div class="card-label">Veículo:</div>
                            <div class="card-value">{{ vehicleData.description }} {{ vehicleData.color }}</div>
                        </div>
                    </div>

                    <div class="info-card">
                        <div class="card-icon">
                            <i class="fas fa-hashtag"></i>
                        </div>
                        <div class="card-content">
                            <div class="card-label">Placa:</div>
                            <div class="card-value">{{ vehicleData.board }}</div>
                        </div>
                    </div>

                    <div class="info-card">
                        <div class="card-icon">
                            <i class="fas fa-list"></i>
                        </div>
                        <div class="card-content">
                            <div class="card-label">Tipo:</div>
                            <div class="card-value">{{ vehicleData.vehicle_type ? vehicleData.vehicle_type.description : 'N/A' }}</div>
                        </div>
                    </div>

                    <div class="info-card">
                        <div class="card-icon">
                            <i class="fas fa-map-marker-alt"></i>
                        </div>
                        <div class="card-content">
                            <div class="card-label">Cidade Destino:</div>
                            <div class="card-value">{{ getDestinationCity() }}</div>
                        </div>
                    </div>

                    <div class="info-card" v-if="hasRides()">
                        <div class="card-icon">
                            <i class="fas fa-users"></i>
                        </div>
                        <div class="card-content">
                            <div class="card-label">Caronas:</div>
                            <div class="card-value">{{ getRideNames() }}</div>
                        </div>
                    </div>

                    <div class="info-card">
                        <div class="card-icon">
                            <i class="fas fa-calendar-alt"></i>
                        </div>
                        <div class="card-content">
                            <div class="card-label">Retirada Estimada:</div>
                            <div class="card-value orange">{{ reserveData.retreat_date }}</div>
                        </div>
                    </div>

                    <div class="info-card">
                        <div class="card-icon">
                            <i class="fas fa-calendar-check"></i>
                        </div>
                        <div class="card-content">
                            <div class="card-label">Retirado Em:</div>
                            <div class="card-value orange">{{ reserveData.checkout ? reserveData.checkout.retreat_at : 'N/A' }}</div>
                        </div>
                    </div>

                    <div class="info-card">
                        <div class="card-icon">
                            <i class="fas fa-calendar-times"></i>
                        </div>
                        <div class="card-content">
                            <div class="card-label">Devolução Estimada:</div>
                            <div class="card-value orange">{{ reserveData.devolution_date }}</div>
                        </div>
                    </div>

                    <div class="info-card">
                        <div class="card-icon">
                            <i class="fas fa-calendar-times"></i>
                        </div>
                        <div class="card-content">
                            <div class="card-label">Devolvido em:</div>
                            <div class="card-value orange">{{ reserveData.checkin ? reserveData.checkin.devoluted_at : 'N/A' }}</div>
                        </div>
                    </div>

                    <div class="info-card" v-if="reserveData.km_traveled">
                        <div class="card-icon">
                            <i class="fas fa-tachometer-alt"></i>
                        </div>
                        <div class="card-content">
                            <div class="card-label">Km Rodados:</div>
                            <div class="card-value blue">{{ formatKilometers(reserveData.km_traveled) }} km</div>
                        </div>
                    </div>
                </div>
            </SectionContainer>

            <!-- Histórico de Viagens -->
            <LastTripsTable
                :vehicle-data="vehicleData"
                :total-trips="totalTrips"
                :pagination-options="paginationOptions"
                @open-trip-details="openModal"
                @load-page="paginate"
            />
        </template>
    </BaseModal>
</template>

<script>
import BaseModal from '../Base/BaseModal.vue';
import SectionContainer from "./Checklist/SectionContainer.vue";
import LastTripsTable from "./Components/LastTripsTable.vue";
import ImageVehicle from "./Components/ImageVehicle.vue";
import { getMessage } from "../../composables/message";

export default {
    name: "DevolutedVehicleModal", // Nome do componente: Modal de Veículo Devolvido
    components: {
        ImageVehicle,
        SectionContainer,
        LastTripsTable,
        BaseModal
    },
    props: {
        modalStatus: { // modalStatus - status do modal se está aberto ou fechado
            type: Boolean,
            default: false
        },
        dataModal: { // dataModal - dados do veículo a serem exibidos no modal
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            paginationOptions: { // paginationOptions - opções de configuração da paginação
                texts: {
                    count: "Mostrando {from} de {to} até {count} de registros",
                },
                page: 1,
                chunk: 4,
                chunksNavigation: "fixed",
            }
        };
    },
    computed: {
        vehicleData() { // vehicleData - dados do veículo computados a partir do dataModal
            return this.dataModal || {};
        },
        reserveData() { // reserveData - dados da reserva do veículo
            return this.vehicleData.reserve_vehicle && this.vehicleData.reserve_vehicle[0] ? this.vehicleData.reserve_vehicle[0] : {};
        },
        totalTrips() { // totalTrips - total de viagens para controle da paginação
            return this.vehicleData.lastTrips ? this.vehicleData.lastTrips.total : 0;
        }
    },
    methods: {
        formatKilometers(value) { // formatKilometers - formata o valor de quilometragem com pontos
            if (!value) return '0';
            return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
        },
        getDestinationCity() { // getDestinationCity - obtém o nome da cidade de destino
            const reserve = this.reserveData;
            if (reserve && reserve.city_destination && reserve.city_destination.city_destination) {
                return reserve.city_destination.city_destination.description;
            }
            return 'N/A';
        },
        hasRides() { // hasRides - verifica se há caronas na reserva
            return this.reserveData.ride && this.reserveData.ride.length > 0;
        },
        getRideNames() { // getRideNames - obtém os nomes das pessoas nas caronas
            if (!this.hasRides()) return '';
            return this.reserveData.ride
                .filter(ride => ride && ride.requester)
                .map(ride => ride.requester.name)
                .join(', ');
        },
        paginate(page) { // paginate - controla a paginação das viagens
            this.$emit("openModals", this.vehicleData.id, this.vehicleData.status, this.vehicleData, page);
        },
        unlock(id, board) { // unlock - desbloqueia o veículo
            this.unlockFunction(id, board);
        },
        unlockFunction(id, board) { // unlockFunction - função para desbloquear veículo via API
            const api = this.$store.state.api + "vehicles/" + id;
            const formData = new FormData();

            formData.append("_method", "PATCH");
            formData.append("board", board);
            formData.append("vehicle_status_id", 1);

            this.$http.post(api, formData)
                .then((response) => {
                    this.$message("Sucesso", `Veículo Desbloqueado`, "success");
                    this.$emit("refresh", false);
                })
                .catch((error) => {
                    const message = error.response.data.errors ? getMessage(error.response.data.errors) : error.response.data;
                    this.$message(null, message, "error");
                });
        },
        openModal(object) { // openModal - abre modal de detalhes de viagem
            this.$emit("OMDCk", object);
        },
        openModalUser(object) { // openModalUser - abre modal do usuário
            this.$emit("OpenModalUser", object);
        },
        clean() { // clean - limpa dados e fecha o modal
            this.$emit("changeModal", false);
        }
    }
};
</script>

<style scoped>
/* Header da reserva */
.reservation-header {
    display: flex;
    gap: 20px;
    margin-bottom: 30px;
    background-color: #f8f9fa;
    border-radius: 12px;
    padding: 20px;
}

.vehicle-section {
    flex: 1;
    max-width: 400px;
}

.status-section {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 10px 0;
}

.status-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.status-badge {
    background-color: #d1e7dd;
    color: #0f5132;
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 600;
    border: 1px solid #badbcc;
}

.reservation-id {
    font-size: 28px;
    font-weight: bold;
    color: #FE8D18;
}

.user-info {
    display: flex;
    align-items: center;
    gap: 12px;
    background-color: white;
    padding: 12px 16px;
    border-radius: 8px;
    border-left: 4px solid #f39c12;
    margin-bottom: 20px;
}

.user-info span {
    flex: 1;
    font-weight: 600;
    color: #495057;
}

.action-buttons {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

/* Grid de cards de informações */
.info-cards-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 15px;
    margin-top: 20px;
}

.info-card {
    background-color: white;
    border-radius: 8px;
    padding: 16px;
    border-left: 4px solid #f39c12;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    display: flex;
    align-items: center;
    gap: 15px;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.info-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.card-icon {
    width: 40px;
    height: 40px;
    background-color: #f39c12;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.card-icon i {
    color: white;
    font-size: 16px;
}

.card-content {
    flex: 1;
}

.card-label {
    font-size: 13px;
    color: #6c757d;
    margin-bottom: 4px;
    font-weight: 500;
}

.card-value {
    font-size: 16px;
    font-weight: 600;
    color: #2c3e50;
}

.card-value.orange {
    color: #f39c12;
}

.card-value.blue {
    color: #FE8D18;
    font-size: 18px;
}

/* Responsividade */
@media (max-width: 768px) {
    .reservation-header {
        flex-direction: column;
        gap: 15px;
    }

    .status-section {
        order: -1;
    }

    .status-info {
        flex-direction: column;
        gap: 10px;
        align-items: flex-start;
    }

    .reservation-id {
        font-size: 24px;
    }

    .user-info {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }

    .info-cards-grid {
        grid-template-columns: 1fr;
    }

    .info-card {
        padding: 12px;
    }

    .action-buttons {
        flex-direction: column;
    }
}

@media (max-width: 576px) {
    .reservation-header {
        padding: 15px;
    }

    .card-icon {
        width: 35px;
        height: 35px;
    }

    .card-icon i {
        font-size: 14px;
    }

    .card-value {
        font-size: 14px;
    }

    .card-value.blue {
        font-size: 16px;
    }
}
</style>



// WEBPACK FOOTER //
// src/components/Modals/DevolutedModal.vue